import { sendEventToLsq, getDeviceAttributes, } from "@oneapp/data";
import { administrativeDivisions, normalizeStateRegionForLsq, } from "./administrativeDivisions";
import { ActivityFieldsEventBuilder, LeadDetailsBuilder } from "./builders";
import { unified_new_lsq_config, unified_new_lsq_config_debug, } from "./staticLsqJson";
import { trackingSourceList } from "./trackingSourceList";
import { fetchGeoForLsqLead } from "./fetchGeoForLsqLead";
const FULL_NAME_REGEX = /^[A-Za-z.]+$/;
let initOptions = null;
export let remoteData = null;
let deviceAttributesCache = null;
let cachedLeadGeo = null;
let leadGeoLoadPromise = null;
function hasUsableLeadGeo(geo) {
    if (!geo || typeof geo !== "object")
        return false;
    const ip = typeof geo.ip === "string" ? geo.ip.trim() : "";
    const region = typeof geo.region === "string" ? geo.region.trim() : "";
    const city = typeof geo.city === "string" ? geo.city.trim() : "";
    return Boolean(ip || region || city);
}
function ensureLeadGeoCached() {
    if (hasUsableLeadGeo(cachedLeadGeo)) {
        return Promise.resolve(cachedLeadGeo);
    }
    if (!leadGeoLoadPromise) {
        leadGeoLoadPromise = fetchGeoForLsqLead()
            .then((geo) => {
            if (hasUsableLeadGeo(geo)) {
                cachedLeadGeo = geo;
            }
            else {
                cachedLeadGeo = null;
            }
            return geo;
        })
            .finally(() => {
            leadGeoLoadPromise = null;
        });
    }
    return leadGeoLoadPromise;
}
export function resolveLsqServicePlatform() {
    const explicit = initOptions?.getLsqServicePlatform?.();
    if (explicit)
        return explicit;
    const p = initOptions?.defaultPlatform;
    if (p === "Web" || p === "MWeb")
        return "web";
    return "android";
}
async function prefetchDeviceAttributes() {
    try {
        const platform = resolveLsqServicePlatform();
        if (platform === "web") {
            deviceAttributesCache = null;
            return;
        }
        const model = initOptions?.getDeviceModel?.()?.trim();
        const clientName = initOptions?.lsqServiceClientName;
        if (!model || !clientName) {
            deviceAttributesCache = null;
            return;
        }
        deviceAttributesCache = await getDeviceAttributes({ model }, {
            clientName,
            headers: { platform },
        });
    }
    catch (e) {
        deviceAttributesCache = null;
        if (initOptions?.isDebug) {
            console.warn("[SqsEvents] device attributes:", e);
        }
    }
}
function filterName(name) {
    const t = name?.trim();
    if (!t || !FULL_NAME_REGEX.test(t))
        return undefined;
    return t;
}
function isValidFirstName(name) {
    if (!name?.trim())
        return undefined;
    const t = name.trim();
    if (!/^[A-Za-z .]+$/.test(t))
        return undefined;
    return t;
}
function getLoginUserSafe() {
    return initOptions?.getLoginUser() ?? null;
}
function programGradeNumber() {
    const g = getLoginUserSafe()?.programGradeNumber;
    return g ?? undefined;
}
function fullName() {
    const u = getLoginUserSafe();
    if (!u)
        return undefined;
    const parts = [filterName(u.firstName), filterName(u.lastName)].filter(Boolean);
    const joined = parts.join(" ").trim();
    return joined.length > 0 ? joined : undefined;
}
function phone() {
    const u = getLoginUserSafe();
    if (!u)
        return undefined;
    const code = u.isdCode?.trim().replace(/^\+/, "") ?? "";
    const number = u.phone?.trim() ?? "";
    if (!code || !number)
        return undefined;
    return `+${code}-${number}`;
}
function unifiedAccountId() {
    const id = getLoginUserSafe()?.unifiedAccountId?.trim();
    return id || undefined;
}
function userType() {
    const isUserTypeFree = initOptions?.getIsUserTypeFree() ?? true;
    const isPackageAmountMoreThenThousand = initOptions?.getIsPackageAmountMoreThenThousand() ?? false;
    return isUserTypeFree && !isPackageAmountMoreThenThousand ? "Free" : "Paid";
}
function loadConfigSafely() {
    if (!initOptions)
        return false;
    try {
        // Mirrors Android:
        // val key = appContext.getString(
        //     if (BuildConfig.DEBUG) R.string.unified_new_lsq_config_debug
        //     else R.string.unified_new_lsq_config)
        // val rawConfig = firebaseRemoteConfig.getString(key)
        const key = initOptions.isDebug
            ? "unified_new_lsq_config_debug"
            : "unified_new_lsq_config";
        const rawConfig = key === "unified_new_lsq_config_debug"
            ? unified_new_lsq_config_debug
            : unified_new_lsq_config;
        remoteData = JSON.parse(rawConfig);
        return remoteData != null;
    }
    catch {
        remoteData = null;
        return false;
    }
}
function requireRemoteConfig() {
    if (remoteData != null)
        return true;
    return loadConfigSafely();
}
function formatUtcDateTimeFromIso(value) {
    if (value == null || value === "")
        return undefined;
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return value;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
export function getUtcDateTime() {
    return formatUtcDateTimeFromIso(new Date().toISOString());
}
function getNetworkType() {
    try {
        return initOptions?.getNetworkType?.() ?? null;
    }
    catch {
        return null;
    }
}
function isLsqDisabled() {
    const user = getLoginUserSafe();
    if (!user)
        return false;
    const config = remoteData?.androidConfig ?? {
        verticalIds: [1],
        isdCode: ["91"],
        isTriggerForPaidUser: true,
    };
    const isLSQEnabled = remoteData?.isLSQEnabled ?? true;
    const verticalId = user.verticalId;
    const verticalMatch = verticalId != null && (config.verticalIds ?? []).includes(verticalId);
    console.log("config.verticalMatch", verticalMatch);
    console.log("config.verticalId", verticalId);
    console.log("config.verticalIds", config.verticalIds);
    const isdRaw = user.isdCode?.trim().replace(/^\+/, "") ?? "";
    const isdCode = isdRaw.length > 0 ? isdRaw : undefined;
    const isdCodeMatch = isdCode != null && (config.isdCode ?? []).includes(isdCode);
    return !(isLSQEnabled && verticalMatch && isdCodeMatch);
}
function isLsqDisableForUltimatePackage() {
    const isTrigger = remoteData?.androidConfig?.isTriggerForPaidUser ?? true;
    const hasTrigger = initOptions?.getHasTriggerLsqForSubscription() ?? false;
    return !isTrigger === hasTrigger;
}
function resolveStateForLead(state) {
    return normalizeStateRegionForLsq(state);
}
function readUnknownString(obj, keys) {
    if (!obj)
        return "";
    for (const k of keys) {
        const v = obj[k];
        if (typeof v === "string" && v.trim())
            return v.trim();
        if (typeof v === "number" && Number.isFinite(v))
            return String(v);
    }
    return "";
}
function applyDeviceAttributesCacheToAppFields(fields, cache) {
    if (!cache || typeof cache !== "object")
        return fields;
    const r = cache;
    const mfr = readUnknownString(r, [
        "manufacturer",
        "Manufacturer",
        "brand",
        "Brand",
        "mx_CustomObject_1",
    ]);
    const marketing = readUnknownString(r, [
        "marketing_name",
        "MarketingName",
        "device_marketing_name",
        "mx_CustomObject_2",
    ]);
    const alt = readUnknownString(r, [
        "alternate_model",
        "model_secondary",
        "secondary_model",
        "mx_CustomObject_3",
    ]);
    return fields.map((f) => {
        if (f.Attribute === "mx_CustomObject_1" && mfr)
            return { ...f, Value: mfr };
        if (f.Attribute === "mx_CustomObject_2" && marketing)
            return { ...f, Value: marketing };
        if (f.Attribute === "mx_CustomObject_3" && alt)
            return { ...f, Value: alt };
        return f;
    });
}
function buildAppMxMobileDeviceSubFieldsFromCacheOnly(cache) {
    if (!cache || typeof cache !== "object")
        return [];
    const r = cache;
    const rows = [];
    for (let i = 1; i <= 5; i += 1) {
        const key = `mx_CustomObject_${i}`;
        rows.push({ Attribute: key, Value: readUnknownString(r, [key]) });
    }
    if (rows.every((x) => !x.Value))
        return [];
    return rows;
}
/** DOM-free access so consumers (e.g. React Native) without `lib: DOM` can typecheck. */
function readGlobalNavigatorUserAgent() {
    const nav = globalThis.navigator;
    const ua = nav?.userAgent;
    return typeof ua === "string" && ua.length > 0 ? ua : undefined;
}
function resolveWebMxMobileDeviceSubFields() {
    const isMweb = initOptions?.defaultPlatform === "MWeb" ||
        initOptions?.isMobileWebViewport?.() === true;
    const ua = readGlobalNavigatorUserAgent();
    if (ua === undefined) {
        return isMweb
            ? [
                { Attribute: "mx_CustomObject_4", Value: "Unknown" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ]
            : [
                { Attribute: "mx_CustomObject_4", Value: "Windows" },
                { Attribute: "mx_CustomObject_5", Value: "Desktop" },
            ];
    }
    if (isMweb) {
        if (/iPhone|iPad|iPod/i.test(ua)) {
            return [
                { Attribute: "mx_CustomObject_4", Value: "iOS" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ];
        }
        if (/Android/i.test(ua)) {
            return [
                { Attribute: "mx_CustomObject_4", Value: "Android" },
                { Attribute: "mx_CustomObject_5", Value: "Mobile" },
            ];
        }
        return [
            { Attribute: "mx_CustomObject_4", Value: "Mobile Web" },
            { Attribute: "mx_CustomObject_5", Value: "Mobile" },
        ];
    }
    if (/Mac OS X|Macintosh/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "macOS" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    if (/Windows/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "Windows" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    if (/Linux/i.test(ua)) {
        return [
            { Attribute: "mx_CustomObject_4", Value: "Linux" },
            { Attribute: "mx_CustomObject_5", Value: "Desktop" },
        ];
    }
    return [
        { Attribute: "mx_CustomObject_4", Value: "Windows" },
        { Attribute: "mx_CustomObject_5", Value: "Desktop" },
    ];
}
function resolveActivityPlatformField(intentPlatform) {
    const trimmed = intentPlatform?.trim();
    if (trimmed)
        return trimmed;
    const def = initOptions?.defaultPlatform;
    if (def === "Web" && initOptions?.isMobileWebViewport?.() === true) {
        return "MWeb";
    }
    return def ?? "App";
}
function buildMxMobileDeviceSubFieldsForLead() {
    if (resolveLsqServicePlatform() === "web") {
        return resolveWebMxMobileDeviceSubFields();
    }
    const fromApp = initOptions?.getAppMxMobileDeviceFields?.() ?? null;
    if (fromApp?.length) {
        return applyDeviceAttributesCacheToAppFields(fromApp, deviceAttributesCache);
    }
    return buildAppMxMobileDeviceSubFieldsFromCacheOnly(deviceAttributesCache);
}
function createDefaultLeadIntent(override, rich) {
    const isWeb = resolveLsqServicePlatform() === "web" ||
        initOptions?.defaultPlatform === "Web" ||
        initOptions?.defaultPlatform === "MWeb";
    const productName = isWeb ? "IL Website" : "Learn (AN)";
    let productOrigin = isWeb
        ? trackingSourceList["Il_website"]
        : trackingSourceList["learn_an"];
    const trackingKey = initOptions?.getTrackingSourceKey?.()?.trim();
    if (trackingKey && trackingSourceList[trackingKey]) {
        productOrigin = trackingSourceList[trackingKey];
    }
    const base = {
        searchBy: "Phone",
        userPhoneNumber: phone(),
        unifiedAccountID: unifiedAccountId(),
        academicGrade: programGradeNumber() ?? undefined,
        productOrigin,
        productName,
        networkType: getNetworkType() ?? undefined,
    };
    if (!rich) {
        return base;
    }
    return {
        ...base,
        ...(cachedLeadGeo?.region
            ? {
                state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
                    cachedLeadGeo?.region ??
                    undefined,
            }
            : {}),
        ...(cachedLeadGeo?.city ? { city: cachedLeadGeo.city } : {}),
        ...(cachedLeadGeo?.ip ? { sourceIpAddress: cachedLeadGeo.ip } : {}),
        ...override,
    };
}
function generateLead(leadIntent, attachMxMobileDeviceToLead, leadDetailMode) {
    if (!remoteData)
        return [];
    const schema = remoteData.schemas?.Lead;
    if (leadDetailMode === "minimal") {
        const local = createDefaultLeadIntent(undefined, false);
        return new LeadDetailsBuilder()
            .put(schema?.searchBy, local.searchBy)
            .put(schema?.userPhoneNumber, local.userPhoneNumber)
            .put(schema?.unifiedAccountID, local.unifiedAccountID)
            .put(schema?.academicGrade, local.academicGrade)
            .put(schema?.productOrLeadOrigin, local.productOrigin)
            .put(schema?.productName, local.productName)
            .put(schema?.networkType, local.networkType)
            .build();
    }
    const local = createDefaultLeadIntent(leadIntent ?? undefined, true);
    const leadDetails = new LeadDetailsBuilder()
        .put(schema?.searchBy, local.searchBy)
        .put(schema?.userPhoneNumber, local.userPhoneNumber)
        .put(schema?.userFullName, local.userFullName)
        .put(schema?.userEmail, local.userEmail)
        .put(schema?.unifiedAccountID, local.unifiedAccountID)
        .put(schema?.academicGrade, local.academicGrade)
        .put(schema?.productOrLeadOrigin, local.productOrigin)
        .put(schema?.utmCampaign, local.utmCampaign)
        .put(schema?.utmMedium, local.utmMedium)
        .put(schema?.utmTerm, local.utmTerm)
        .put(schema?.utmContent, local.utmContent)
        .put(schema?.referrerURL, local.referrerUrl)
        .put(schema?.pageURL, local.pageUrl)
        .put(schema?.state, resolveStateForLead(local.state))
        .put(schema?.city, local.city)
        .put(schema?.productName, local.productName)
        .put(schema?.sourceIpAddress, local.sourceIpAddress)
        .put(schema?.optInDate, local.optInDate)
        .put(schema?.networkType, local.networkType);
    const shouldAttachMxMobileDevice = attachMxMobileDeviceToLead === true ||
        initOptions?.attachMxMobileDeviceToAllLeads === true;
    if (shouldAttachMxMobileDevice) {
        const deviceSubFields = buildMxMobileDeviceSubFieldsForLead();
        if (deviceSubFields.length) {
            leadDetails.putMxMobileDevice(schema?.deviceInfoObject, deviceSubFields);
        }
    }
    return leadDetails.build();
}
function mergeFlashcardIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        firstName: intent.firstName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
    };
}
function resolveProductVerticalForVideo(intentProductVertical) {
    const map = remoteData?.productVerticalMap;
    if (!map)
        return undefined;
    const intentId = intentProductVertical?.trim();
    if (intentId) {
        const fromIntent = map[intentId]?.trim();
        if (fromIntent)
            return fromIntent;
    }
    const vid = getLoginUserSafe()?.verticalId;
    if (vid == null)
        return undefined;
    return map[String(vid)]?.trim() || undefined;
}
function mergeActSelfLearnVideoIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
async function emitActivity(eventId, fields, leadIntent, attachMxMobileDeviceToLead, leadDetailMode = "minimal") {
    if (!initOptions || eventId == null)
        return;
    await sendEventToLsq({
        LeadDetails: generateLead(leadIntent, attachMxMobileDeviceToLead, leadDetailMode),
        Activity: {
            ActivityEvent: String(eventId),
            ActivityDateTime: getUtcDateTime(),
            Fields: fields,
        },
    }, { clientName: initOptions.lsqManagementClientName });
}
function mergeActSignUpIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
        state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
            cachedLeadGeo?.region ??
            undefined,
        city: cachedLeadGeo?.city ?? undefined,
    };
}
function mergeActMarketingIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
        state: administrativeDivisions.filter((d) => d.name?.toLowerCase() === cachedLeadGeo?.region?.toLowerCase())[0]?.name ??
            cachedLeadGeo?.region ??
            undefined,
        city: cachedLeadGeo?.city ?? undefined,
    };
}
function mergeActTestIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        testName: intent.testName,
        examType: intent.examType,
        testMode: intent.testMode,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        platform,
        testDate: formatUtcDateTimeFromIso(intent.testDate) ?? intent.testDate,
    };
}
function mergeActMasterClassIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActSelfLearnStoryIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountID: intent.unifiedAccountID ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActTalkToExpertIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        phone: intent.phone,
        examName: intent.examName,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActDoubtIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        doubtsSubject: intent.subject,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActScoreRegistrationIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function mergeActAddToCartIntent(intent) {
    const platform = resolveActivityPlatformField(intent.platform);
    return {
        ...intent,
        studentName: intent.studentName ?? fullName(),
        userType: intent.userType ?? userType(),
        unifiedAccountId: intent.unifiedAccountId ?? unifiedAccountId(),
        productVertical: resolveProductVerticalForVideo(intent.productVertical),
        grade: intent.grade ?? programGradeNumber() ?? undefined,
        platform,
    };
}
function init(options) {
    initOptions = options;
    loadConfigSafely();
    void prefetchDeviceAttributes();
    void ensureLeadGeoCached();
}
function actSelfLearnFlashcard(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Flashcards;
            const merged = mergeFlashcardIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.flashcard_subject, merged.flashcardSubject)
                .put(schema?.flashcard_chapter, merged.flashcardChapter)
                .put(schema?.firstName, isValidFirstName(merged.firstName))
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Flashcards;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnFlashcard:", e);
            }
        }
    })();
}
function actSelfLearnVideo(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Video;
            const merged = mergeActSelfLearnVideoIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studyVideo, merged.studyVideo)
                .put(schema?.durationWatched, merged.durationWatched)
                .put(schema?.subject, merged.subject)
                .put(schema?.chapter, merged.chapter)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Video;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnVideo:", e);
            }
        }
    })();
}
function actSelfLearnStory(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Self_Learn_Story;
            const merged = mergeActSelfLearnStoryIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.story_subject, merged.subject)
                .put(schema?.story_chapter, merged.chapter)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Self_Learn_Story;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSelfLearnStory:", e);
            }
        }
    })();
}
function actMarketingActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            await ensureLeadGeoCached();
            const schema = remoteData.schemas?.ACT_marketing;
            const merged = mergeActMarketingIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.utmSource, merged.utmSource)
                .put(schema?.utmCampaign, merged.utmCampaign)
                .put(schema?.utmMedium, merged.utmMedium)
                .put(schema?.utmContent, merged.utmContent)
                .put(schema?.utmId, merged.utmId)
                .put(schema?.utmTerm, merged.utmTerm)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.referralPage, merged.referralPage)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountID)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_marketing;
            await emitActivity(eventId, jsonObject, leadIntent, true, "marketingSignup");
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actMarketingActivity:", e);
            }
        }
    })();
}
function actTests(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Tests;
            const merged = mergeActTestIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.tests_exam_type, merged.examType)
                .put(schema?.tests_test_name, merged.testName)
                .put(schema?.tests_score, merged.score)
                .put(schema?.percentage, merged.percentage)
                .put(schema?.tests_rank, merged.rank)
                .put(schema?.tests_subject, merged.subjects)
                .put(schema?.userType, merged.userType)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.testMode, merged.testMode)
                .put(schema?.testDate, merged.testDate)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.reportURL, merged.reportURL)
                .build();
            const eventId = remoteData.eventIds?.Act_Tests;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actTests:", e);
            }
        }
    })();
}
function actMasterClass(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Master_Class_App;
            const merged = mergeActMasterClassIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.recorded_class, merged.recordedClass)
                .put(schema?.className, merged.className)
                .put(schema?.watchTimeInSeconds, merged.watchTimeInSeconds)
                .put(schema?.subject, merged.subject)
                .put(schema?.facultyName, merged.facultyName)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountID, merged.unifiedAccountID)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Master_Class_App;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actMasterClass:", e);
            }
        }
    })();
}
function actTalkToExpertActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ACT_TALK_TO_EXPERT;
            const merged = mergeActTalkToExpertIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.requestedDate, merged.requestedDate)
                .put(schema?.phone, merged.phone)
                .put(schema?.preferableDateTime, merged.preferableDateTime)
                .put(schema?.attendee, merged.attendee)
                .put(schema?.language, merged.language)
                .put(schema?.remarks, merged.remarks)
                .put(schema?.decisionMaker, merged.decisionMaker)
                .put(schema?.examName, merged.examName)
                .put(schema?.grade, merged.grade)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.intentOfTheStudent, merged.intentOfTheStudent)
                .put(schema?.interestInOnlineCoaching, merged.interestInOnlineCoaching)
                .put(schema?.howDoYouStudyOnline, merged.howDoYouStudyOnline)
                .put(schema?.difficultSubjectForStudent, merged.difficultSubjectForStudent)
                .put(schema?.preferredLearningMode, merged.preferredLearningMode)
                .put(schema?.schoolName, merged.schoolName)
                .put(schema?.inMobiusSchoolCode, merged.inMobiusSchoolCode)
                .put(schema?.latitudeLong, merged.latitudeLong)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_TALK_TO_EXPERT;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actTalkToExpertActivity:", e);
            }
        }
    })();
}
function actDoubtActivity(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled() || isLsqDisableForUltimatePackage())
                return;
            const schema = remoteData.schemas?.Act_Doubts;
            const merged = mergeActDoubtIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.doubtsSubject, merged.subject)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.Act_Doubts;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actDoubtActivity:", e);
            }
        }
    })();
}
function actSignUp(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            await ensureLeadGeoCached();
            const schema = remoteData.schemas?.ACT_SignUp;
            const merged = mergeActSignUpIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.state, merged.state)
                .put(schema?.city, merged.city)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.utmSource, merged.utmSource)
                .put(schema?.utmCampaign, merged.utmCampaign)
                .put(schema?.utmMedium, merged.utmMedium)
                .put(schema?.utmContent, merged.utmContent)
                .put(schema?.utmId, merged.utmId)
                .put(schema?.utmTerm, merged.utmTerm)
                .put(schema?.referralPage, merged.referralPage)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.parentOrStudent, merged.parentOrStudent)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ACT_SignUp;
            await emitActivity(eventId, jsonObject, leadIntent, true, "marketingSignup");
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] actSignUp:", e);
            }
        }
    })();
}
function appScoreRegistration(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ActScoreRegistration;
            const merged = mergeActScoreRegistrationIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.grade, merged.grade)
                .put(schema?.examName, merged.examName)
                .put(schema?.testName, merged.testName)
                .put(schema?.utmUrl, merged.utmUrl)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ActScoreRegistration;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] appScoreRegistration:", e);
            }
        }
    })();
}
function appAddToCart(intent, leadIntent) {
    void (async () => {
        try {
            if (!initOptions)
                return;
            const configReady = requireRemoteConfig();
            if (!configReady || !remoteData) {
                if (initOptions.isDebug) {
                    console.warn("[SqsEvents] LSQ config unavailable. Event aborted.");
                }
                return;
            }
            if (isLsqDisabled())
                return;
            const schema = remoteData.schemas?.ActAddToCart;
            const merged = mergeActAddToCartIntent(intent);
            const jsonObject = new ActivityFieldsEventBuilder()
                .put(schema?.notes, merged.notes)
                .put(schema?.status, merged.status)
                .put(schema?.owner, merged.owner)
                .put(schema?.packageAmountRs, merged.packageAmountRs)
                .put(schema?.discountAmountRs, merged.discountAmountRs)
                .put(schema?.orderAmountRs, merged.orderAmountRs)
                .put(schema?.orderDateTime, merged.orderDateTime)
                .put(schema?.paymentPlatform, merged.paymentPlatform)
                .put(schema?.couponCode, merged.couponCode)
                .put(schema?.orderId, merged.orderId)
                .put(schema?.packageName, merged.packageName)
                .put(schema?.packageValidity, merged.packageValidity)
                .put(schema?.billingPhoneNumber, merged.billingPhoneNumber)
                .put(schema?.billingName, merged.billingName)
                .put(schema?.billingEmail, merged.billingEmail)
                .put(schema?.studentName, merged.studentName)
                .put(schema?.userType, merged.userType)
                .put(schema?.unifiedAccountId, merged.unifiedAccountId)
                .put(schema?.productVertical, merged.productVertical)
                .put(schema?.grade, merged.grade)
                .put(schema?.platform, merged.platform)
                .build();
            const eventId = remoteData.eventIds?.ActAddToCart;
            await emitActivity(eventId, jsonObject, leadIntent);
        }
        catch (e) {
            if (initOptions?.isDebug) {
                console.warn("[SqsEvents] appAddToCart:", e);
            }
        }
    })();
}
export const SqsEvents = {
    init,
    actMarketingActivity,
    actTests,
    actMasterClass,
    actSelfLearnStory,
    actSelfLearnFlashcard,
    actSelfLearnVideo,
    actTalkToExpertActivity,
    actDoubtActivity,
    actSignUp,
    appScoreRegistration,
    appAddToCart,
    getUtcDateTime,
};
